/**
 * Pure concern-scoped Season resolvers + window predicates.
 *
 * This module is **genuinely pure**: it performs no I/O, reads no globals,
 * and never calls `new Date()`. The current instant is always injected as an
 * explicit `now: Date` argument so the same code path is shared by web,
 * mobile, and backend and is trivially property-testable with a deterministic
 * clock. (Resolved Design Decision 2; "Season Propagation & Consumer
 * Contract".)
 *
 * The operative year is always the `competitionYear` of the single `ACTIVE`
 * Season — never derived from the wall-clock calendar year (R2.1, R2.3). The
 * concern resolvers select a Season by its intake / judging window so that
 * during the rollover overlap a *different* Season can be intake-active than
 * is judging-active, while each individual concern stays single-valued
 * (refined invariant: same-concern windows across Seasons must not overlap).
 *
 * Requirements: 2.1, 2.3, 3.3, 3.4, 7.3, 7.4
 */

import type { Season, SeasonWindow } from './season';

// ---------------------------------------------------------------------------
// Result union

/**
 * The explicit, total result of resolving a Season for a concern.
 *
 *  - `resolved`            — exactly one Season satisfies the concern.
 *  - `unresolved`          — zero Seasons satisfy the concern. Callers MUST
 *                            handle this explicitly (e.g. render a no-anchor
 *                            state) and MUST NOT substitute a calendar-derived
 *                            value (R2.3, R3.4).
 *  - `invariant-violation` — more than one Season satisfies the concern,
 *                            which breaks a single-valued invariant. The
 *                            resolver surfaces this rather than silently
 *                            selecting one (R3.3).
 */
export type SeasonResolution =
  | { kind: 'resolved'; season: Season }
  | { kind: 'unresolved' }
  | { kind: 'invariant-violation'; reason: string };

// ---------------------------------------------------------------------------
// Window predicates (pure)

/**
 * Read the effective window for a concern. A missing window attribute
 * (pre-backfill rows) is treated identically to a fully-null window, i.e.
 * always open. (R7.2 back-compat.)
 */
function effectiveWindow(window: SeasonWindow | null | undefined): SeasonWindow {
  return window ?? { opensAt: null, closesAt: null };
}

/**
 * Is `now` within `window`? A `null` bound means "open on that side", so
 * `{ opensAt: null, closesAt: null }` is always open. Bounds are inclusive of
 * `opensAt` and exclusive of `closesAt`.
 *
 * Pure: depends only on its arguments. Unparseable bounds are treated as
 * absent (open) rather than throwing, so a malformed row degrades to the
 * back-compat "open" behavior instead of crashing the resolver.
 */
function isWithinWindow(window: SeasonWindow, now: Date): boolean {
  const nowMs = now.getTime();

  if (window.opensAt !== null) {
    const opensMs = Date.parse(window.opensAt);
    if (!Number.isNaN(opensMs) && nowMs < opensMs) return false;
  }
  if (window.closesAt !== null) {
    const closesMs = Date.parse(window.closesAt);
    if (!Number.isNaN(closesMs) && nowMs >= closesMs) return false;
  }
  return true;
}

/**
 * Is the Season's **intake** window open at `now`? Pure function of
 * `(season, now)`. A null/absent intake window is treated as open. ARCHIVED
 * Seasons are never intake-open. (R7.3, Property 5.)
 */
export function isIntakeOpen(season: Season, now: Date): boolean {
  if (season.status === 'ARCHIVED') return false;
  return isWithinWindow(effectiveWindow(season.intakeWindow), now);
}

/**
 * Is the Season's **judging** window open at `now`? Pure function of
 * `(season, now)`. A null/absent judging window is treated as open. ARCHIVED
 * Seasons are never judging-open. (R7.4, Property 5.)
 */
export function isJudgingOpen(season: Season, now: Date): boolean {
  if (season.status === 'ARCHIVED') return false;
  return isWithinWindow(effectiveWindow(season.judgingWindow), now);
}

// ---------------------------------------------------------------------------
// Concern resolvers (pure)

/**
 * Resolve the single operative/default Season — the one row whose status is
 * `ACTIVE`.
 *
 *  - exactly one ACTIVE  → `resolved`
 *  - zero ACTIVE         → `unresolved` (never a calendar fallback — R2.3, R3.4)
 *  - more than one ACTIVE → `invariant-violation` (never silently selects — R3.3)
 *
 * Pure: a function of the provided `seasons` only. (Property 1.)
 */
export function resolveActiveSeason(seasons: Season[]): SeasonResolution {
  const active = seasons.filter((s) => s.status === 'ACTIVE');
  if (active.length === 1) {
    return { kind: 'resolved', season: active[0] };
  }
  if (active.length === 0) {
    return { kind: 'unresolved' };
  }
  return {
    kind: 'invariant-violation',
    reason: `Expected exactly one ACTIVE Season, found ${active.length}`,
  };
}

/**
 * Resolve the Season whose **intake** window contains `now`.
 *
 *  - exactly one intake-open → `resolved`
 *  - zero intake-open        → `unresolved`
 *  - more than one           → `invariant-violation` (same-concern windows
 *                              must not overlap — refined R7 invariant /
 *                              Property 8)
 *
 * Pure: a function of `(seasons, now)` only; never `new Date()`.
 */
export function resolveIntakeSeason(
  seasons: Season[],
  now: Date,
): SeasonResolution {
  const open = seasons.filter((s) => isIntakeOpen(s, now));
  if (open.length === 1) {
    return { kind: 'resolved', season: open[0] };
  }
  if (open.length === 0) {
    return { kind: 'unresolved' };
  }
  return {
    kind: 'invariant-violation',
    reason:
      `Expected at most one Season with an open intake window at ${now.toISOString()},`
      + ` found ${open.length} (same-concern windows must not overlap)`,
  };
}

/**
 * The `Active_Intake_Season`s at `now`: exactly those Seasons whose intake
 * open-window includes `now` per `isIntakeOpen`, in ascending `competitionYear`
 * so the result is deterministic.
 *
 * Unlike `resolveIntakeSeason` (single-valued, surfacing an invariant violation
 * on overlap), this resolver is **plural** — it returns every intake-open
 * Season so callers can surface a control per active season (member-onboarding-flow
 * Req 3.1). The result is empty if and only if no Season is intake-open at `now`
 * (Req 3.2), which callers treat as "no application/profile surface".
 *
 * Pure: a function of `(seasons, now)` only; never `new Date()`. The input array
 * is not mutated.
 *
 * Requirements: 3.1, 3.2, 3.3, 6.5 (member-onboarding-flow)
 */
export function activeIntakeSeasons(
  seasons: readonly Season[],
  now: Date,
): Season[] {
  return seasons
    .filter((s) => isIntakeOpen(s, now))
    .slice()
    .sort((a, b) => a.competitionYear - b.competitionYear);
}

/**
 * Resolve the Season whose **judging** window contains `now`.
 *
 *  - exactly one judging-open → `resolved`
 *  - zero judging-open        → `unresolved`
 *  - more than one            → `invariant-violation` (same-concern windows
 *                               must not overlap — refined R7 invariant /
 *                               Property 8)
 *
 * Pure: a function of `(seasons, now)` only; never `new Date()`.
 */
export function resolveJudgingSeason(
  seasons: Season[],
  now: Date,
): SeasonResolution {
  const open = seasons.filter((s) => isJudgingOpen(s, now));
  if (open.length === 1) {
    return { kind: 'resolved', season: open[0] };
  }
  if (open.length === 0) {
    return { kind: 'unresolved' };
  }
  return {
    kind: 'invariant-violation',
    reason:
      `Expected at most one Season with an open judging window at ${now.toISOString()},`
      + ` found ${open.length} (same-concern windows must not overlap)`,
  };
}

// ---------------------------------------------------------------------------
// Applicant season derivation (pure)

/**
 * Total reducer over a **non-empty** candidate list returning the candidate
 * with the greatest `competitionYear`. Because candidates are unique by their
 * `competitionYear` PK, the result is single-valued (no `competitionYear` tie
 * is possible among distinct Seasons). Pure; never mutates its input.
 *
 * This is applied **only** to break earliest-closing ties in
 * `deriveApplicationSeason` (R3.3) — never as a primary selector.
 */
function greatestYear(candidates: readonly Season[]): Season {
  return candidates.reduce((a, b) =>
    b.competitionYear > a.competitionYear ? b : a,
  );
}

/**
 * The intake `closesAt` bound for a Season, or `null` when absent/open. A
 * missing intake window (pre-backfill rows) or a null `closesAt` both mean
 * "open on the close side", matching the `isIntakeOpen` back-compat behavior.
 */
function intakeClosesAt(season: Season): string | null {
  return season.intakeWindow?.closesAt ?? null;
}

/**
 * Derive the single Season an Applicant's application is scoped to at `now`,
 * from the `Active_Intake_Season` set. Pure, total, and deterministic: it never
 * calls `new Date()` and never mutates its input. Composed from
 * `activeIntakeSeasons` + `resolveActiveSeason` (the SSOT), so it can never
 * disagree with them.
 *
 *   - 0 active intake seasons → `{ kind: 'unresolved' }`        (R4.1, R4.3)
 *   - 1 active intake season  → `{ kind: 'resolved', season }`  (R2.1)
 *   - >1 → exactly one selected by the R3 tie-break:            (R3.1–R3.4)
 *       1. operative-year match: the candidate whose `competitionYear` equals
 *          the single `ACTIVE` Season's `competitionYear`
 *          (`resolveActiveSeason`), if any;                     (R3.1)
 *       2. else the candidate with the earliest **non-null** intake `closesAt`;
 *                                                               (R3.2)
 *       3. else (tie on earliest `closesAt`, or every candidate `closesAt`
 *          null) the candidate with the greatest `competitionYear`. (R3.3)
 *
 * Returns only `resolved` or `unresolved` — it always selects exactly one
 * candidate when the set is non-empty, so it never produces
 * `invariant-violation`.
 *
 * Pure: a function of `(seasons, now)` only; never `new Date()`. Bounds are
 * parsed defensively, inheriting the `isIntakeOpen` back-compat behavior.
 *
 * Requirements: 2.1, 2.2, 2.3, 3.1, 3.2, 3.3, 3.4, 4.1, 4.3
 */
export function deriveApplicationSeason(
  seasons: readonly Season[],
  now: Date,
): SeasonResolution {
  const candidates = activeIntakeSeasons(seasons, now); // asc competitionYear
  if (candidates.length === 0) {
    return { kind: 'unresolved' };
  }
  if (candidates.length === 1) {
    return { kind: 'resolved', season: candidates[0] };
  }

  // R3.1 — operative-year match wins.
  const active = resolveActiveSeason([...seasons]);
  if (active.kind === 'resolved') {
    const match = candidates.find(
      (c) => c.competitionYear === active.season.competitionYear,
    );
    if (match) {
      return { kind: 'resolved', season: match };
    }
  }

  // R3.2 — earliest NON-NULL intake closesAt.
  const withClose = candidates.filter((c) => intakeClosesAt(c) !== null);
  if (withClose.length > 0) {
    const earliest = withClose.reduce((a, b) =>
      Date.parse(intakeClosesAt(a)!) <= Date.parse(intakeClosesAt(b)!) ? a : b,
    );
    const earliestMs = Date.parse(intakeClosesAt(earliest)!);
    const tied = withClose.filter(
      (c) => Date.parse(intakeClosesAt(c)!) === earliestMs,
    );
    // R3.3 — break earliest-closing ties by greatest competitionYear.
    return { kind: 'resolved', season: greatestYear(tied) };
  }

  // R3.3 — every candidate closesAt null → greatest competitionYear.
  return { kind: 'resolved', season: greatestYear(candidates) };
}
